import { Link } from "@tanstack/react-router";

/**
 * Coach workspace sub-tab strip — the shared nav across the
 * `/coach` list pages and `/coach/review`.
 *
 * Ports the legacy `setCoachTab` tab strip (`js/coaching.js`).
 * Legacy tab set: `review · roster · notes · playlists · clips ·
 * summaries · engagement · settings`. M6b adds the final tab,
 * `settings`. Goals is deliberately NOT a tab — legacy
 * `VALID_COACH_TABS` has none; goal cards live inside Roster.
 *
 * Routing shape (M6a, non-disruptive to M4/M5):
 *   - `/coach?tab=roster|notes|playlists|clips|summaries|engagement`
 *     — the tabbed shell renders the matching list page. `roster`
 *     is the default (no `?tab=` ⇒ roster), mirroring legacy.
 *   - `/coach/review` stays its OWN path (the M4/M5 surface, with
 *     its `match_id`/`note_id`/`clip_id` schema untouched). The
 *     "Review" tab is a link to that path, not a `?tab=review`.
 *
 * Both surfaces render this strip so the workspace feels unified.
 * The active tab is computed from the current location, passed in
 * by each host (the shell knows its `?tab=`; Coach Review passes
 * `"review"`).
 */

export type CoachTab =
  | "roster"
  | "matches"
  | "notes"
  | "playlists"
  | "clips"
  | "summaries"
  | "engagement"
  | "settings"
  | "review";

/** Tab order + labels. Roster leads the strip (matches the shell's
 * default tab — `?tab=` absent ⇒ Roster — so a fresh `/coach` nav
 * lands on Roster AND the strip's first slot lines up with the
 * default page). Review follows, then the rest of the list pages,
 * with Settings last. */
const TABS: ReadonlyArray<{ id: CoachTab; label: string }> = [
  { id: "roster", label: "Roster" },
  { id: "review", label: "Review" },
  { id: "matches", label: "Matches" },
  { id: "notes", label: "Notes" },
  { id: "playlists", label: "Playlists" },
  { id: "clips", label: "Clips" },
  { id: "summaries", label: "Match Summaries" },
  { id: "engagement", label: "Engagement" },
  { id: "settings", label: "Settings" },
];

export type CoachTabStripProps = {
  /** The currently-active tab — the host computes it (the shell
   * from its `?tab=`, Coach Review hardcodes `"review"`). */
  active: CoachTab;
};

export function CoachTabStrip({ active }: CoachTabStripProps) {
  return (
    <nav
      className="flex gap-1 overflow-x-auto rounded-md border border-rule bg-bg-card p-1"
      role="tablist"
      aria-label="Coach workspace"
      data-testid="coach-tab-strip"
    >
      {TABS.map((tab) => {
        const isActive = tab.id === active;
        // Review is its own path; the list pages are `/coach?tab=`.
        const linkProps =
          tab.id === "review"
            ? ({ to: "/coach/review" } as const)
            : tab.id === "roster"
              ? // Roster is the default — no `?tab=` param.
                ({ to: "/coach", search: {} } as const)
              : ({ to: "/coach", search: { tab: tab.id } } as const);
        return (
          <Link
            key={tab.id}
            {...linkProps}
            role="tab"
            aria-selected={isActive}
            data-coach-tab={tab.id}
            data-active={isActive}
            className={[
              "shrink-0 rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-white"
                : "text-ink-muted hover:text-ink hover:bg-surface",
            ].join(" ")}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
